import React, { useState, useMemo } from 'react';
import {
    FileSpreadsheet, Download, X, CheckCircle2,
    Calendar, History, CheckSquare, Layers, Filter, Sparkles
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { hadActionToday, hasActionHistory } from '../config/prospects';

const ProspectExportModal = ({
    isOpen,
    onClose,
    allProspects = [],
    filteredProspects = [],
    selectedIds = new Set(),
    onExport
}) => {
    // Calculs des différentes sélections
    const todayProspects = useMemo(
        () => allProspects.filter(hadActionToday),
        [allProspects]
    );

    const withHistoryProspects = useMemo(
        () => allProspects.filter(hasActionHistory),
        [allProspects]
    );

    const selectedProspects = useMemo(
        () => allProspects.filter(p => selectedIds.has(p._id)),
        [allProspects, selectedIds]
    );

    // Déterminer l'option par défaut
    const defaultScope = selectedIds.size > 0
        ? 'selected'
        : (filteredProspects.length < allProspects.length ? 'filtered' : 'with_history');

    const [scope, setScope] = useState(defaultScope);

    // Synchroniser si la sélection change
    React.useEffect(() => {
        if (selectedIds.size > 0) {
            setScope('selected');
        } else if (scope === 'selected') {
            setScope('filtered');
        }
    }, [selectedIds.size]);

    const todayDateStr = new Date().toISOString().slice(0, 10);

    const getFileName = (currentScope) => {
        switch (currentScope) {
            case 'today': return `prospects-traites-aujourdhui-${todayDateStr}.xlsx`;
            case 'with_history': return `prospects-avec-echanges-${todayDateStr}.xlsx`;
            case 'selected': return `prospects-selection-manuelle-${todayDateStr}.xlsx`;
            case 'filtered': return `prospects-filtres-${todayDateStr}.xlsx`;
            default: return `prospects-xoralearn-complet-${todayDateStr}.xlsx`;
        }
    };

    const [fileName, setFileName] = useState(() => getFileName(defaultScope));

    const handleScopeChange = (newScope) => {
        setScope(newScope);
        setFileName(getFileName(newScope));
    };

    if (!isOpen) return null;

    const getTargetList = () => {
        switch (scope) {
            case 'selected': return selectedProspects;
            case 'today': return todayProspects;
            case 'with_history': return withHistoryProspects;
            case 'filtered': return filteredProspects;
            default: return allProspects;
        }
    };

    const targetList = getTargetList();

    const handleConfirm = () => {
        onExport(targetList, fileName);
        onClose();
    };

    const options = [
        {
            id: 'filtered',
            title: 'Vue actuelle filtrée',
            count: filteredProspects.length,
            desc: 'Les prospects correspondant à vos filtres et recherche actuels.',
            icon: Filter,
            tone: 'blue'
        },
        {
            id: 'today',
            title: 'Traités aujourd\'hui',
            count: todayProspects.length,
            desc: 'Uniquement les prospects ayant eu un appel, message ou action aujourd\'hui.',
            icon: Calendar,
            tone: 'amber'
        },
        {
            id: 'with_history',
            title: 'Avec échanges uniquement (Déjà contactés)',
            count: withHistoryProspects.length,
            desc: 'Exclut les prospects vierges sans aucun contact (masque les "Contacté" non appelés).',
            icon: History,
            tone: 'emerald'
        },
        {
            id: 'selected',
            title: 'Sélection manuelle (Cases cochées)',
            count: selectedProspects.length,
            desc: `${selectedIds.size} prospect(s) actuellement coché(s) dans le tableau.`,
            icon: CheckSquare,
            tone: 'indigo',
            disabled: selectedIds.size === 0
        },
        {
            id: 'all',
            title: 'Toute la base de données',
            count: allProspects.length,
            desc: 'Tous les prospects sans aucune exclusion ni filtre.',
            icon: Layers,
            tone: 'slate'
        }
    ];

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0, scale: 0.96, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="bg-white border border-slate-200 rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden flex flex-col"
                >
                    {/* Header */}
                    <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/60">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600 shadow-2xs">
                                <FileSpreadsheet size={20} />
                            </div>
                            <div>
                                <h3 className="text-sm font-black text-slate-900 tracking-tight flex items-center gap-2">
                                    Exporter vers Excel (.xlsx)
                                </h3>
                                <p className="text-[11px] font-medium text-slate-500">
                                    Choisissez exactement les prospects que vous voulez exporter
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition"
                        >
                            <X size={16} />
                        </button>
                    </div>

                    {/* Body */}
                    <div className="p-6 space-y-4">
                        <div className="space-y-2">
                            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500">
                                Quel périmètre souhaitez-vous exporter ?
                            </label>

                            <div className="space-y-2">
                                {options.map(opt => {
                                    const Icon = opt.icon;
                                    const active = scope === opt.id;
                                    const disabled = opt.disabled;

                                    return (
                                        <button
                                            key={opt.id}
                                            type="button"
                                            disabled={disabled}
                                            onClick={() => handleScopeChange(opt.id)}
                                            className={`w-full p-3 rounded-xl border text-left transition flex items-start gap-3 relative ${
                                                active
                                                    ? 'border-emerald-500 bg-emerald-50/40 shadow-2xs ring-1 ring-emerald-500/20'
                                                    : disabled
                                                    ? 'opacity-40 border-slate-100 bg-slate-50/50 cursor-not-allowed'
                                                    : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/60'
                                            }`}
                                        >
                                            <div className={`mt-0.5 w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                                                active
                                                    ? 'bg-emerald-600 text-white'
                                                    : 'bg-slate-100 text-slate-600'
                                            }`}>
                                                <Icon size={14} />
                                            </div>

                                            <div className="flex-1 min-w-0 pr-16">
                                                <div className="flex items-center gap-2">
                                                    <span className={`text-xs font-bold ${active ? 'text-emerald-950' : 'text-slate-800'}`}>
                                                        {opt.title}
                                                    </span>
                                                </div>
                                                <p className="text-[11px] text-slate-500 line-clamp-1 mt-0.5">
                                                    {opt.desc}
                                                </p>
                                            </div>

                                            <div className="absolute right-3 top-3">
                                                <span className={`text-[11px] font-mono font-bold px-2 py-0.5 rounded-md border ${
                                                    active
                                                        ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                                                        : 'bg-slate-100 text-slate-600 border-slate-200'
                                                }`}>
                                                    {opt.count}
                                                </span>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Nom du fichier */}
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                Nom du fichier téléchargé
                            </label>
                            <input
                                type="text"
                                value={fileName}
                                onChange={e => setFileName(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono font-medium text-slate-800 outline-none focus:border-emerald-500 focus:bg-white transition"
                            />
                        </div>

                        {/* Note d'information */}
                        <div className="p-3 bg-slate-50 border border-slate-200/80 rounded-xl flex items-start gap-2.5">
                            <Sparkles size={14} className="text-emerald-600 shrink-0 mt-0.5" />
                            <p className="text-[11px] text-slate-600 leading-relaxed">
                                Le fichier Excel contiendra <strong className="text-slate-800">{targetList.length} prospect(s)</strong> avec les <strong>3 feuilles</strong> : Tableau complet avec comptes-rendus compilés, Historique granulaire et le Guide système.
                            </p>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-3.5 bg-slate-50/80 border-t border-slate-100 flex items-center justify-end gap-2.5">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-800 hover:bg-slate-200/60 rounded-lg transition"
                        >
                            Annuler
                        </button>
                        <button
                            type="button"
                            disabled={targetList.length === 0}
                            onClick={handleConfirm}
                            className="inline-flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg transition shadow-sm disabled:opacity-40"
                        >
                            <Download size={14} />
                            <span>Exporter {targetList.length} prospect(s) (.xlsx)</span>
                        </button>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default ProspectExportModal;
